import { useIsAuthenticated } from "#hooks/use-auth"
import { getStandardRetryDelay, getStandardRetryLogic } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { homeFinancialSDK } from "#sdk/sdk"
import type { Account } from "@home-finance-manager/shared/types/accounts"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/**
 * Get the current account from localStorage
 */
const getStoredCurrentAccount = (): Account | null => {
  try {
    const accountData = localStorage.getItem("current_account")
    if (!accountData) return null
    return JSON.parse(accountData)
  } catch {
    return null
  }
}

/**
 * Store current account in localStorage
 */
const storeCurrentAccount = (account: Account): void => {
  localStorage.setItem("current_account", JSON.stringify(account))
}

/**
 * Hook to get all accounts
 */
export function useAccounts() {
  const queryClient = useQueryClient()
  const isAuthenticated = useIsAuthenticated()

  return useQuery({
    queryKey: queryKeys.accounts.lists(),
    queryFn: async () => {
      try {
        return await homeFinancialSDK.accounts.getAccounts()
      } catch (error) {
        if (error instanceof Error && error.message.includes("Authentication token not found")) {
          return []
        }
        throw error
      }
    },
    enabled: isAuthenticated,
    // Use cached data if available (from auth response)
    placeholderData: () => {
      const cached = queryClient.getQueryData<Account[]>(queryKeys.accounts.lists())
      return cached || undefined
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to get the current account
 */
export function useCurrentAccount() {
  return useQuery({
    queryKey: queryKeys.accounts.current(),
    queryFn: () => {
      const stored = getStoredCurrentAccount()
      if (!stored) {
        // Try to get from accounts list
        return null
      }
      return stored
    },
    staleTime: Infinity,
    initialData: getStoredCurrentAccount,
    // Ensure the query refetches when the cache is updated
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to set the current account
 */
export function useSetCurrentAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (account: Account) => {
      storeCurrentAccount(account)
      return account
    },
    onSuccess: (account) => {
      queryClient.setQueryData(queryKeys.accounts.current(), account)
    },
  })
}

/**
 * Hook to create a new account
 */
export function useCreateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => homeFinancialSDK.accounts.createAccount(name),
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
      showToast.success(`Account "${account.name}" created successfully`)
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to update an account
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      homeFinancialSDK.accounts.updateAccount(id, name),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.detail(variables.id) })
      showToast.success(`Account "${variables.name}" updated successfully`)
    },
  })
}

/**
 * Hook to set default account
 */
export function useSetDefaultAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => homeFinancialSDK.accounts.setDefaultAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all })
      showToast.success("Default account updated successfully")
    },
    retry: getStandardRetryLogic(),
    retryDelay: getStandardRetryDelay(),
  })
}

/**
 * Hook to invite a user to an account
 */
export function useInviteUser() {
  return useMutation({
    mutationFn: ({ accountId, email, role }: { accountId: number; email: string; role?: string }) =>
      homeFinancialSDK.accounts.inviteUser(accountId, email, role),
  })
}
